import type { ChangeEvent, MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import {
  assigneeName,
  priorityColor,
  slaCountdown,
  statusColor,
  statusDot,
  statusLabel,
} from '@/lib/format';
import type { Ticket, TeamMember, AwayPeriod } from '@/lib/queries';
import { highlightSegments } from '@/lib/search-query';
import { AwayDot, buildAwayLabel } from '@/components/AwayBadge';
import { RelativeTime } from '@/components/RelativeTime';
import type { TicketAction } from '@/lib/api';
import { useTicketAction } from '@/lib/use-ticket-action';
import { useAccess } from '@/lib/use-access';
import { cn } from '@/lib/utils';
import {
  Hand,
  CheckCircle2,
  AlertOctagon,
  Archive,
  ArchiveRestore,
  Ban,
  Paperclip,
  Moon,
  Frown,
  Bot,
  AlarmClock,
  MoreVertical,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { UserAvatar } from '@/components/UserAvatar';
import { useTicketViewers } from '@/lib/workspace-presence-context';
import { useReminderTicketIds } from '@/lib/use-reminders';

const CLOSED = new Set(['resolved', 'closed']);

// Two clicks closer together than this count as one. When a row's action takes
// it out of the list, the rows below slide up under the cursor, so the second
// click of an accidental double-click would hit the NEXT ticket's button.
const QUICK_ACTION_LOCK_MS = 400;
let lastQuickActionAt = 0;

// Single-row, dense list-style ticket entry. ~52px tall.
// Hover reveals quick action icons (Claim / Resolve / Escalate) on the right;
// the relative-time text is replaced by them on hover so width stays stable.
export function TicketRow({
  ticket,
  teamMembersBySlackId,
  selected,
  onToggleSelect,
  hasAttachment,
  awayPeriods,
  linkSearch,
  highlightTerms,
}: {
  ticket: Ticket;
  teamMembersBySlackId?: Map<string, TeamMember>;
  /** When true, the row is in a multi-selected state (bulk actions). */
  selected?: boolean;
  /** Provided iff the dashboard wants to render checkboxes. Stops Link nav. */
  onToggleSelect?: () => void;
  /** When true, renders a small paperclip glyph in the indicator
   *  cluster so users can see "there's a file in here" without
   *  expanding. The set membership is computed in the parent via
   *  `useTicketsHaveAttachments(ticketIds)` — one bulk query, not
   *  per-row. Defaults to false; omit the prop on callers that don't
   *  care. */
  hasAttachment?: boolean;
  /** Currently-active away periods keyed by `team_member_slack_id`.
   *  When the assignee has an active entry, an amber dot is overlaid
   *  on the assignee avatar and a tooltip surfaces the human-readable
   *  "X is on leave · Y covering until …" label. One Map per parent
   *  page (from `useActiveAwayPeriods()`), shared across all rows. */
  awayPeriods?: Map<string, AwayPeriod>;
  /** Search results only: a query string appended to the ticket link
   *  ("?find=invoice&m=<message id>") so the ticket opens with Ctrl+F's
   *  find-in-conversation already filled in. */
  linkSearch?: string;
  /** Search results only: words to mark inside the subject. */
  highlightTerms?: string[];
}) {
  const access = useAccess();
  // Robot mail (migration 080) is not on anyone's clock: no SLA chip, a small bot
  // icon instead. The SLA flags on such a ticket are noise nobody should chase.
  const robot = ticket.is_automated_sender === true;
  const slaBreached =
    !robot && (!!ticket.sla_first_response_breached || !!ticket.sla_resolution_breached);
  const lastActivity =
    ticket.last_team_message_at ?? ticket.last_client_message_at ?? ticket.updated_at;

  // Pick the most-relevant SLA clock to surface:
  //   - If the ticket is closed (resolved/closed), no SLA badge — the clock
  //     stops the moment status flips. Without this guard, tickets that got
  //     resolved without a recorded outbound reply (e.g. resolved straight
  //     from triage, merged-out, or any path where workflow 02 didn't fire)
  //     keep showing "overdue Xh" forever, which is misleading.
  //   - Otherwise: first-response clock until we've replied, then resolution.
  const sla = robot || CLOSED.has(ticket.status ?? '')
    ? null
    : !ticket.first_response_at
      ? slaCountdown(ticket.sla_first_response_due_at)
      : slaCountdown(ticket.sla_resolution_due_at);
  const senderLabel =
    ticket.sender_name?.trim() || ticket.sender_email || 'Unknown sender';
  const intent = (ticket.intent_summary ?? '').trim().length > 3 ? (ticket.intent_summary ?? '').trim() : '';
  const assigneeLabel = assigneeName(ticket.assignee_slack_id, teamMembersBySlackId);
  const isClosed = CLOSED.has(ticket.status ?? '');
  const isArchived = !!ticket.archived_at;
  // Live presence: who else (if anyone) is currently looking at this ticket?
  // Sourced from the workspace-wide presence channel mounted in
  // WorkspacePresenceProvider. Self is filtered out by the context.
  const liveViewers = useTicketViewers(ticket.id);
  // A follow-up reminder of mine is waiting on this ticket.
  const hasReminder = useReminderTicketIds().has(ticket.id);

  // The row changes the moment a quick action is clicked and the server
  // confirms in the background (lib/use-ticket-action.ts). Nothing here is
  // disabled while it runs, so the next click is never blocked behind it.
  const { run } = useTicketAction();
  const runAction = (action: TicketAction) => {
    // The list closes the gap under the cursor, so the second click of a
    // double-click would land on the NEXT ticket's button. Swallow it.
    const now = Date.now();
    if (now - lastQuickActionAt < QUICK_ACTION_LOCK_MS) return;
    lastQuickActionAt = now;
    void run(ticket.id, action, undefined, { subject: ticket.subject });
  };

  // Action click handler: stop the surrounding Link from navigating.
  const fire = (action: TicketAction) => (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    runAction(action);
  };

  // The checkbox sits OUTSIDE the Link element so its click never navigates.
  // We use a flex wrapper with two children: [checkbox] + [link row].
  const handleCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    onToggleSelect?.();
  };

  // Resolve the away period for the assignee (if any) ONCE so the same
  // computed `awayLabel` can drive both the mobile-top avatar and the
  // desktop right-rail avatar. The lookup is a single O(1) Map.get; the
  // parent page provides the Map via useActiveAwayPeriods.
  const awayPeriod = ticket.assignee_slack_id
    ? awayPeriods?.get(ticket.assignee_slack_id) ?? null
    : null;
  // One table drives both the desktop hover bar and the mobile overflow menu,
  // so the two can't drift. `label` is the accessible name (prefixed onto the
  // ticket subject); `title` is the longer hover explanation where one helps.
  const allQuickActions: Array<{
    action: TicketAction;
    label: string;
    title: string;
    icon: typeof Hand;
    tone?: 'danger' | 'positive';
  }> = isArchived
    ? [{ action: 'unarchive', label: 'Unarchive', title: 'Restore to the dashboard', icon: ArchiveRestore }]
    : isClosed
      ? [{ action: 'archive', label: 'Archive', title: 'Hide from dashboard', icon: Archive }]
      : [
          { action: 'claim', label: 'Claim', title: 'Assign this ticket to yourself', icon: Hand },
          { action: 'resolve', label: 'Resolve', title: 'Mark resolved', icon: CheckCircle2, tone: 'positive' },
          { action: 'escalate', label: 'Escalate', title: 'Escalate', icon: AlertOctagon, tone: 'danger' },
          {
            action: 'close',
            label: 'Dismiss',
            title:
              'Dismiss as marketing / spam / no action — archives and hides. Use Resolve for legitimate tickets.',
            icon: Ban,
            tone: 'danger',
          },
          { action: 'archive', label: 'Archive', title: 'Hide from dashboard', icon: Archive },
        ];

  // Only the actions this person's role allows in THIS ticket's inbox
  // (migration 078). The dispatcher refuses the rest regardless.
  const quickActions = allQuickActions.filter((a) =>
    access.canAction(ticket.inbox_id, a.action, {
      actorSlackId: access.access?.member.slack_user_id,
      assigneeSlackId: ticket.assignee_slack_id,
    }),
  );

  let awayLabel: string | null = null;
  if (awayPeriod && ticket.assignee_slack_id) {
    const awayMember = teamMembersBySlackId?.get(ticket.assignee_slack_id);
    const backupMember = teamMembersBySlackId?.get(awayPeriod.backup_slack_id);
    awayLabel = buildAwayLabel({
      awayName: awayMember?.name ?? awayMember?.email ?? ticket.assignee_slack_id,
      backupName:
        backupMember?.name ?? backupMember?.email ?? awayPeriod.backup_slack_id,
      endsAt: awayPeriod.ends_at,
    });
  }

  return (
    <div
      className={cn(
        // `relative` + `group/row` live here rather than on the <Link> because
        // the quick-action bar is now a SIBLING of the link, not a descendant
        // of it — see the comment on that block below.
        'group group/row relative flex items-stretch gap-1 rounded-md border border-transparent transition-colors duration-100',
        selected
          ? 'border-foreground/30 bg-accent/40'
          : 'hover:bg-accent/60 hover:border-border/60',
      )}
    >
      {onToggleSelect ? (
        <label
          // Top-aligned with a small mt-3 on mobile so the checkbox sits
          // level with the status pill / subject — centering it on a
          // 3-line row puts the box awkwardly in dead space. On sm+ the
          // row is single-line at 52px, so center-align is the natural
          // resting position.
          className="flex shrink-0 cursor-pointer items-start pl-3 pt-3 sm:items-center sm:pt-0"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={!!selected}
            onChange={handleCheckbox}
            className="h-4 w-4 cursor-pointer accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            // Name the ticket, not just the action. Fifty checkboxes all
            // announcing "Select ticket" told a screen-reader user nothing
            // about what they were about to bulk-dismiss.
            aria-label={`${selected ? 'Deselect' : 'Select'} ticket: ${ticket.subject ?? 'no subject'}`}
          />
        </label>
      ) : null}
      <Link
        to={`/tickets/${ticket.id}${linkSearch ?? ''}`}
        className={cn(
          // min-h on mobile so the row can grow when the subject wraps to 2
          // lines; fixed 52px on sm+ so the desktop list stays scannable.
          //
          // `min-w-0` is what keeps the row inside the page: a flex item can't
          // shrink below its content unless told it may, and the sender line
          // now carries the AI's one-line summary (a long run that never wraps).
          // Without it the whole link grew to that text's width and pushed the
          // status / age columns off the right edge, out of line from row to row.
          'relative flex min-h-[52px] min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-md px-3 py-2 sm:h-[52px] sm:py-0',
          // No reserved lane for the action bar. An earlier pass added
          // `sm:pr-20` so the hover bar wouldn't cover anything — but that
          // left 80px of permanent dead space at the right edge, so the
          // timestamp floated in the middle of it instead of aligning to
          // anything. The bar overlays the timestamp on hover instead, which
          // is fine because urgency lives in the always-visible SLA chip in
          // the indicator cluster, outside the bar's reach.
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
        )}
      >
      <Badge
        variant="pill"
        className={cn('w-9 shrink-0 self-start justify-center sm:self-auto', priorityColor(ticket.priority))}
      >
        {ticket.priority ?? '—'}
      </Badge>

      <div className="min-w-0 flex-1">
        {/* Mobile-only status pill row. The full status name
            ("waiting on client", "in progress") doesn't fit inline on
            phones — at 360px it gets chopped to "waiting on cl…" with
            no useful truncation. Lifting it to its own row above the
            subject as a coloured pill keeps the WHOLE word visible,
            and the colour does most of the at-a-glance work anyway
            (blue=new, purple=waiting, emerald=resolved). Hidden on
            sm+ where the dedicated right-rail status badge takes over. */}
        {ticket.status || ticket.assignee_slack_id ? (
          <div className="mb-1 flex items-center gap-1.5 sm:hidden">
            {/* Assignee avatar — leads the pill row on mobile so the
                "who owns this" cue reads first, left-to-right, before
                the status. Shares the hoisted `awayLabel` with the
                desktop avatar in the right-rail cluster so the away
                overlay + tooltip behave identically across breakpoints. */}
            {ticket.assignee_slack_id ? (
              <span
                className="relative inline-flex shrink-0"
                title={awayLabel ?? undefined}
              >
                <UserAvatar
                  slackId={ticket.assignee_slack_id}
                  teamMembersBySlackId={teamMembersBySlackId}
                  size="sm"
                />
                {awayLabel ? <AwayDot label={awayLabel} /> : null}
              </span>
            ) : null}
            {ticket.status ? (
              <Badge
                variant="pill"
                className={cn('whitespace-nowrap', statusColor(ticket.status))}
              >
                {statusLabel(ticket.status)}
              </Badge>
            ) : null}
            {ticket.client_tier && ticket.client_tier !== 'standard' ? (
              <span className="text-3xs uppercase tracking-wide text-muted-foreground">
                {ticket.client_tier}
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="flex min-w-0 items-start gap-2 sm:items-center">
          {/* Unread: the client wrote since you last opened this (migration 081). */}
          {ticket.unread === true ? (
            <span
              className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-tint-info-fg sm:mt-0"
              role="img"
              aria-label="Unread"
              title="Unread: the client wrote since you last opened it"
            />
          ) : null}
          <span
            className={cn(
              // Mobile: wrap to 2 lines so long subjects like
              // "Email/SMS work flows triggered by form submission" stay
              // readable instead of getting chopped at "submis".
              // Desktop: single-line truncate as before.
              // `min-w-0` + `break-words` let the subject shrink/wrap
              // even when it's a single super-long unbroken token (e.g.
              // a bare URL inserted by some clients into the subject line) —
              // without these, the flex child keeps its intrinsic width
              // and pushes the trailing tier badge off the row.
              'min-w-0 break-words text-sm leading-snug',
              ticket.unread === true ? 'font-semibold text-foreground' : 'font-medium text-foreground/90',
              'line-clamp-2 sm:line-clamp-1',
            )}
          >
            {highlightTerms && highlightTerms.length > 0 && ticket.subject
              ? highlightSegments(ticket.subject, highlightTerms).map((seg, i) =>
                  seg.hit ? (
                    <mark
                      key={i}
                      className="rounded-sm bg-tint-warning px-0.5 text-tint-warning-fg"
                    >
                      {seg.text}
                    </mark>
                  ) : (
                    <span key={i}>{seg.text}</span>
                  ),
                )
              : (ticket.subject ?? '(no subject)')}
          </span>
          {/* Tier chip stays inline on desktop only — on mobile it lives
              next to the status pill above so the subject gets the full
              row width to wrap into. */}
          {ticket.client_tier && ticket.client_tier !== 'standard' ? (
            <span className="hidden shrink-0 text-3xs uppercase tracking-wide text-muted-foreground sm:inline">
              {ticket.client_tier}
            </span>
          ) : null}
        </div>
        {/* Sender row. On mobile we still append `· time` here so the
            agent sees freshness inline; the STATUS has been lifted to
            its own pill row above. On sm+ both time + status live in
            the dedicated right-rail column.

            `min-w-0` on the flex container + on the senderLabel span is
            mandatory: without it, a long client email keeps its
            intrinsic width and pushes the time off the row. The
            senderLabel shrinks and ellipsises instead. */}
        <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
          <span className="min-w-0 truncate md:max-w-[50%] md:shrink-0">{senderLabel}</span>
          {/* The AI's one-line read of what they want (captured at triage, never shown
              before). Same line as the sender, so the row doesn't grow; desktop only. */}
          {intent ? (
            <span className="hidden min-w-0 flex-1 truncate text-muted-foreground/70 md:inline" title={intent}>
              <span aria-hidden>· </span>
              {intent}
            </span>
          ) : null}
          {/* Mobile-only inline metadata — just the time now */}
          <span className="shrink-0 sm:hidden" aria-hidden>·</span>
          <RelativeTime
            value={lastActivity}
            className="shrink-0 sm:hidden"
          />
        </div>
      </div>

      {/* ── Right-hand columns ───────────────────────────────────────────────
          Every slot below has a FIXED width and is always rendered, even when
          empty. That's the whole point: previously these were ordinary flex
          children that appeared and disappeared per row, so a ticket with no
          assignee and no SLA pushed its status badge left while the row beneath
          it sat further right. Nothing formed a vertical line, and the list
          read as jitter.

          Reading order matches how a ticket actually gets triaged:
            is it late? → what kind of thing is it? → whose is it? → what
            state? → how fresh?

          Only ONE thing on the row is allowed to be a saturated block: the
          priority chip on the left, or the SLA chip when a ticket is late.
          Everything else is muted, so the exceptions are what catch the eye. */}
      <div className="flex shrink-0 items-center gap-2">
        {/* Secondary metadata — attachment, snooze, tone, live viewers.
            All one muted weight and one size, in a fixed slot, because none of
            them should compete with lateness or status. Each used to be its own
            differently-coloured chip; the snooze one even carried a text label.

            `role="img"` on each: ARIA prohibits an accessible name on a
            role-less generic, so the earlier bare-<span> versions were silently
            dropped by NVDA and JAWS. */}
        <div className="hidden w-12 shrink-0 items-center justify-end gap-1 text-muted-foreground md:flex">
          {hasReminder ? (
            <span title="You set a follow-up reminder on this ticket" className="inline-flex">
              <AlarmClock className="h-3.5 w-3.5 shrink-0" role="img" aria-label="Follow-up reminder set" />
            </span>
          ) : null}
          {robot ? (
            <span title="Automated sender: no Slack card and no SLA clock" className="inline-flex">
              <Bot className="h-3.5 w-3.5 shrink-0" role="img" aria-label="Automated sender" />
            </span>
          ) : null}
          {liveViewers.length > 0 ? (
            <span
              title={
                liveViewers.length === 1
                  ? `${liveViewers[0].name} is viewing this ticket right now`
                  : `${liveViewers.length} teammates are viewing this ticket right now`
              }
              className="relative flex h-2 w-2"
              role="img"
              aria-label={
                liveViewers.length === 1
                  ? `${liveViewers[0].name} is viewing this ticket`
                  : `${liveViewers.length} teammates are viewing this ticket`
              }
            >
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-tint-success-fg opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-tint-success-fg" />
            </span>
          ) : null}
          {ticket.sentiment === 'negative' || ticket.sentiment === 'frustrated' || ticket.sentiment === 'angry' ? (
            // Was a filled critical-red circle with a "!" — as loud as a
            // breached SLA, for what is only an advisory AI read of tone.
            <Frown
              className="h-3.5 w-3.5 shrink-0"
              role="img"
              aria-label={`Claude read this message as ${ticket.sentiment} in tone`}
            />
          ) : null}
          {ticket.snoozed_until && new Date(ticket.snoozed_until) > new Date() ? (
            <Moon
              className="h-3.5 w-3.5 shrink-0"
              role="img"
              aria-label={`Snoozed until ${new Date(ticket.snoozed_until).toLocaleString()}`}
            />
          ) : null}
          {hasAttachment ? (
            <Paperclip className="h-3.5 w-3.5 shrink-0" role="img" aria-label="Has attachments" />
          ) : null}
        </div>

        {/* Lateness. The one exception that earns saturated colour, because it
            is the only thing here that means "act now". Fixed slot so the chip
            never shifts the columns after it. */}
        <div className="hidden w-16 shrink-0 justify-end sm:flex">
          {slaBreached || sla?.state === 'breached' ? (
            <span
              className="inline-flex h-5 items-center whitespace-nowrap rounded px-1.5 text-3xs font-semibold tabular-nums bg-tint-critical text-tint-critical-fg ring-1 ring-inset ring-tint-critical-ring"
              title={sla ? `SLA ${sla.label}` : 'This ticket has breached its SLA'}
            >
              {sla?.shortLabel ?? 'SLA'}
            </span>
          ) : sla?.state === 'soon' ? (
            <span
              className="inline-flex h-5 items-center whitespace-nowrap rounded px-1.5 text-3xs font-semibold tabular-nums bg-tint-warning text-tint-warning-fg ring-1 ring-inset ring-tint-warning-ring"
              title={`SLA ${sla.label}`}
            >
              {sla.shortLabel}
            </span>
          ) : null}
        </div>

        {/* Owner. Always a 20px slot so the avatar column is a straight line
            whether or not the ticket is assigned. */}
        <div className="hidden w-5 shrink-0 sm:block">
          {ticket.assignee_slack_id ? (
            <span className="relative inline-flex" title={awayLabel ?? undefined}>
              <UserAvatar
                slackId={ticket.assignee_slack_id}
                teamMembersBySlackId={teamMembersBySlackId}
                size="sm"
              />
              {awayLabel ? <AwayDot label={awayLabel} /> : null}
            </span>
          ) : (
            <span className="sr-only">{assigneeLabel || 'Unassigned'}</span>
          )}
        </div>

        {/* Status + timestamp share a wrapper that fades on hover, because the
            quick-action bar occupies exactly this space (≈176px of buttons
            against 184px of columns). Fading what's underneath is what lets
            the action bar drop its background plate entirely.

            `pointer-events-none` while faded so a stray click can't land on an
            invisible element; `aria-hidden` is deliberately NOT set, because
            the row still needs to announce its status to a screen reader that
            never triggers hover in the first place. */}
        <div
          className={cn(
            'hidden items-center gap-2 transition-opacity sm:flex',
            'group-hover/row:pointer-events-none group-hover/row:opacity-0',
            'group-focus-within/row:pointer-events-none group-focus-within/row:opacity-0',
          )}
        >
          {/* State. Hue survives in the dot so the column is still scannable at
              speed, but the label is plain foreground text — a filled pill on
              every single row was the second full-colour system that made this
              cluster read as noise. Left-aligned in a fixed slot so the labels
              themselves line up down the list. */}
          <div className="flex w-28 shrink-0 items-center gap-1.5">
            <span
              aria-hidden
              className={cn('h-1.5 w-1.5 shrink-0 rounded-full', statusDot(ticket.status))}
            />
            <span className="truncate text-2xs text-foreground/70">
              {statusLabel(ticket.status)}
            </span>
          </div>

          {/* Freshness. ONE line — this column previously rendered the full
              "13 Aug 2026, 03:35 pm" plus an SLA line beneath it, three lines
              of content inside a fixed 52px row, which overflowed and clipped
              the status badge beside it. Compact, never-wrapping, right-aligned
              with tabular figures so the column forms a clean vertical edge.
              The full datetime is still one hover away. */}
          <div className="flex w-16 shrink-0 justify-end pr-1 text-xs tabular-nums text-muted-foreground">
            <RelativeTime value={lastActivity} compact className="whitespace-nowrap" />
          </div>
        </div>
      </div>
    </Link>

      {/* ── Quick actions ────────────────────────────────────────────────────
          Three things were wrong with the previous version of this block.

          1. It lived INSIDE the <Link>. Interactive content nested in an <a>
             is invalid per the HTML content model, and Enter on a focused
             button also triggered the anchor navigation. It's now a sibling.
          2. It was `opacity-0` with only a `group-hover` reveal. `opacity-0`
             doesn't remove anything from the tab order, so a keyboard user
             tabbed through five invisible buttons per row — 250 invisible
             stops on a 50-row dashboard, four of which mutate ticket state
             irreversibly (WCAG 2.4.7). `group-focus-within` now reveals it.
          3. Touch devices have no hover at all, so the entire fast path was
             unreachable on a phone. Below `sm` the actions render as an
             always-visible overflow menu instead.

          Each button also names the ticket it acts on, because "Claim" alone
          is meaningless when a screen reader reads fifty of them in a row. */}
      <div
        className={cn(
          'absolute inset-y-0 right-2 hidden items-center gap-0.5 sm:flex',
          'opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-within/row:opacity-100',
          // No backdrop plate. An earlier version painted `bg-accent/95` here
          // so the icons wouldn't sit on top of the text beneath — but the row
          // itself is only `bg-accent/60` on hover, so the bar came out
          // LIGHTER than the row and read as a floating grey box, which was
          // especially obvious in dark mode.
          //
          // The status and timestamp columns underneath fade out instead (see
          // the `group-hover/row:opacity-0` wrapper around them). Same
          // occlusion, nothing to colour-match, and it degrades correctly in
          // both themes because there's no second surface to keep in step.
        )}
      >
        {quickActions.map((a) => (
          <button
            key={a.action}
            type="button"
            title={a.title}
            aria-label={`${a.label}: ${ticket.subject ?? 'no subject'}`}
            onClick={fire(a.action)}
            className={cn(
              'inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors',
              // `bg-foreground/10` rather than `bg-background`: in dark mode
              // the background token is near-black, so hovering a button on an
              // already-lightened row punched a hole in it. A foreground tint
              // lifts in both themes without needing a per-theme value.
              'hover:bg-foreground/10',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:cursor-not-allowed disabled:opacity-50',
              a.tone === 'danger' && 'hover:text-tint-critical-fg',
              a.tone === 'positive' && 'hover:text-tint-success-fg',
              !a.tone && 'hover:text-foreground',
            )}
          >
            <a.icon className="h-4 w-4" aria-hidden />
          </button>
        ))}
      </div>

      {/* Touch path: a real menu, always visible, 44px tall. */}
      <div className="flex items-center pr-1 sm:hidden">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Actions for ticket: ${ticket.subject ?? 'no subject'}`}
              className="inline-flex h-11 w-9 items-center justify-center rounded-md text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            >
              <MoreVertical className="h-4 w-4" aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {quickActions.map((a) => (
              <DropdownMenuItem
                key={a.action}
                onClick={() => runAction(a.action)}
                className="gap-2 py-2"
              >
                <a.icon className="h-4 w-4 shrink-0" aria-hidden />
                {a.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

// Back-compat re-export so any stragglers don't break.
export const TicketCard = TicketRow;
